import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
}

const sizeClasses: Record<Size, string> = {
  sm: 'h-10 px-4 text-sm',
  md: 'h-12 px-5 text-base',
  lg: 'h-14 px-6 text-base font-semibold',
};

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-accent text-white active:bg-accent-hover disabled:bg-disabled disabled:text-white/80',
  secondary:
    'bg-transparent text-text-primary border border-border active:bg-surface disabled:opacity-50',
  // ghost — третичная кнопка («Закрыть» в M4/M5): минимальный визуальный вес,
  // приглушённый серый текст, без border. Phase 2.6 fix: было text-accent
  // (оранжевый), что ставило ghost в одну весовую категорию с primary —
  // нарушало 3-уровневую иерархию действий в модалке.
  ghost:
    'bg-transparent text-text-secondary active:opacity-80 disabled:opacity-50',
};

export function Button({
  variant = 'primary',
  size = 'lg',
  loading = false,
  disabled = false,
  fullWidth = false,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      className={cn(
        'inline-flex items-center justify-center rounded-2xl no-tap-highlight',
        'transition-colors duration-150',
        'disabled:cursor-not-allowed',
        sizeClasses[size],
        variantClasses[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <span>{children}</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
}
